import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { ROLES } from "../constants";
import { Shield, UserPlus, Trash2, KeyRound } from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import ConfigMetas from "../components/ConfigMetas";
import ConfigRoteiro from "../components/ConfigRoteiro";
import ConfirmDialog from "../components/ConfirmDialog";

async function adminApi(action, params) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch("/api/admin", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ action, ...params }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Erro desconhecido");
  return json;
}

export default function Team({ currentRole, metas = [], setMetas, roteiro = [], setRoteiro, userName = "" }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  // Create user
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "comercial" });
  const [formBusy, setFormBusy] = useState(false);
  const [formMsg, setFormMsg] = useState("");

  // Reset password
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers() {
    const { data } = await supabase.from("profiles").select("*").order("created_at", { ascending: true });
    setMembers(data || []);
    setLoading(false);
  }

  // O papel muda pelo servidor (api/admin), que confere quem pode dar o quê.
  // O navegador não grava `role` direto: o banco recusa.
  async function changeRole(id, newRole) {
    setSaving(id);
    setFormMsg("");
    try {
      await adminApi("set-role", { userId: id, role: newRole });
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role: newRole } : m)));
    } catch (err) {
      setFormMsg(err.message);
    }
    setSaving(null);
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormMsg("");
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setFormMsg("Preencha todos os campos obrigatórios");
      return;
    }
    if (form.password.length < 6) {
      setFormMsg("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    setFormBusy(true);
    try {
      await adminApi("create-user", {
        email: form.email,
        password: form.password,
        fullName: form.name,
        role: form.role,
      });
      await loadMembers();
      setForm({ name: "", email: "", password: "", role: "comercial" });
      setShowCreate(false);
      setFormMsg("Usuário criado com sucesso! No primeiro login, será solicitada a troca de senha.");
    } catch (err) {
      setFormMsg(err.message);
    }
    setFormBusy(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await adminApi("delete-user", { userId: deleteTarget.id });
      setMembers((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setFormMsg("Usuário excluído com sucesso.");
    } catch (err) {
      setFormMsg(err.message);
    }
    setDeleteTarget(null);
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!resetTarget || !resetPwd || resetPwd.length < 6) return;
    setResetBusy(true);
    try {
      await adminApi("reset-password", { userId: resetTarget.id, newPassword: resetPwd });
      setFormMsg(`Senha de ${resetTarget.full_name} redefinida. No próximo login será solicitada nova troca.`);
      setResetTarget(null);
      setResetPwd("");
    } catch (err) {
      setFormMsg(err.message);
    }
    setResetBusy(false);
  }

  const isAdmin = currentRole === "admin";
  const canManage = currentRole === "admin" || currentRole === "gerente";

  const roleColor = { admin: "#C02646", gerente: "#005BED", comercial: "#46C026" };
  const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid rgba(128,128,128,0.08)", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", background: "rgba(128,128,128,0.08)", color: "#555" };

  if (loading) return <div style={{ padding: 20, color: "#94a3b8" }}>Carregando equipe...</div>;

  return (
    <div>
      <GuideBanner
        title="Equipe & Acessos"
        text={
          canManage
            ? "Crie contas para sua equipe com e-mail e senha. Admin tem acesso total, Gerente vê todos os dados e cria comerciais, Comercial só vê os seus."
            : "Veja os membros da equipe e seus papéis."
        }
      />

      {/* Metas: quem define é a gerência. Fica aqui porque é a tela que já
          conhece a equipe — e porque meta que o cobrado edita não é meta. */}
      {canManage && (
        <div style={{ margin: "14px 0" }}>
          <ConfigMetas
            metas={metas}
            setMetas={setMetas}
            equipe={members.filter((m) => m.role === "comercial" || m.role === "gerente")}
            userName={userName}
          />
        </div>
      )}

      {/* Roteiro: o que a conversa precisa cobrir. Fica ao lado das metas
          porque as duas coisas são a mesma decisão — o que se cobra da
          abordagem: quantas, e sobre o quê. */}
      {canManage && (
        <div style={{ margin: "14px 0" }}>
          <ConfigRoteiro roteiro={roteiro} setRoteiro={setRoteiro} userName={userName} />
        </div>
      )}

      {formMsg && !showCreate && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 500,
          background: formMsg.includes("sucesso") ? "#ecfdf5" : "#fef2f2",
          color: formMsg.includes("sucesso") ? "#059669" : "#dc2626",
          border: `1px solid ${formMsg.includes("sucesso") ? "#a7f3d0" : "#fecaca"}`,
        }}>
          {formMsg}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>{members.length} membro(s)</div>
        {canManage && (
          <button
            onClick={() => { setShowCreate(!showCreate); setFormMsg(""); }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            <UserPlus size={14} /> Criar Usuário
          </button>
        )}
      </div>

      {/* Create User Form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={{
          background: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af", marginBottom: 12 }}>
            <UserPlus size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Novo Usuário do Sistema
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Nome Completo *</label>
              <input style={inputStyle} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Nome completo" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>E-mail *</label>
              <input style={inputStyle} type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="email@empresa.com" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>
                <KeyRound size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
                Senha *
              </label>
              <input style={inputStyle} type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Papel</label>
              <select style={inputStyle} value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
                {Object.entries(ROLES)
                  .filter(([k]) => isAdmin || ROLES[k].level <= ROLES[currentRole].level)
                  .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          {formMsg && showCreate && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#dc2626", fontWeight: 500 }}>{formMsg}</div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button type="button" onClick={() => { setShowCreate(false); setFormMsg(""); }} style={{
              padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff",
              color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>Cancelar</button>
            <button type="submit" disabled={formBusy} style={{
              padding: "7px 16px", borderRadius: 6, border: "none",
              background: "#059669", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
              opacity: formBusy ? 0.6 : 1,
            }}>
              {formBusy ? "Criando..." : "Criar Usuário"}
            </button>
          </div>
        </form>
      )}

      {/* Members Table */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Nome</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Papel</th>
              <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 11 }}>Criado em</th>
              {canManage && <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 600, color: "#475569", fontSize: 11 }}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "10px 14px", fontWeight: 500 }}>{m.full_name}</td>
                <td style={{ padding: "10px 14px" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "3px 10px", borderRadius: 20,
                    background: `${roleColor[m.role] || "#94a3b8"}18`,
                    color: roleColor[m.role] || "#94a3b8",
                    fontSize: 11, fontWeight: 600,
                  }}>
                    <Shield size={11} />
                    {ROLES[m.role]?.label || (m.role === "pendente" ? "Aguardando liberação" : m.role)}
                  </span>
                </td>
                <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 12 }}>
                  {m.created_at ? new Date(m.created_at).toLocaleDateString("pt-BR") : "—"}
                </td>
                {canManage && (
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    {(ROLES[m.role]?.level || 0) < (ROLES[currentRole]?.level || 0) ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        <select
                          value={m.role}
                          onChange={(e) => changeRole(m.id, e.target.value)}
                          disabled={saving === m.id}
                          style={{
                            padding: "4px 8px", borderRadius: 6, border: "1px solid #e2e8f0",
                            fontSize: 12, cursor: "pointer", background: saving === m.id ? "#f1f5f9" : "#fff",
                          }}
                        >
                          {Object.entries(ROLES)
                            .filter(([k]) => isAdmin || ROLES[k].level < ROLES[currentRole].level)
                            .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <button
                          title="Redefinir senha"
                          onClick={() => { setResetTarget(m); setResetPwd(""); }}
                          style={{ padding: 4, borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <KeyRound size={13} color="#f59e0b" />
                        </button>
                        <button
                          title="Excluir usuário"
                          onClick={() => setDeleteTarget(m)}
                          style={{ padding: 4, borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                          <Trash2 size={13} color="#dc2626" />
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Excluir Usuário"
        message={`Tem certeza que deseja excluir "${deleteTarget?.full_name}"? Esta ação é irreversível.`}
        confirmLabel="Excluir"
        danger
        onConfirm={handleDelete}
      />

      {/* Reset Password Modal */}
      {resetTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <form onSubmit={handleResetPassword} style={{ background: "#fff", borderRadius: 12, padding: 24, width: 380, boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
              <KeyRound size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Redefinir Senha
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              Definir nova senha para <strong>{resetTarget.full_name}</strong>. O usuário será obrigado a trocar no próximo login.
            </div>
            <input
              type="password"
              value={resetPwd}
              onChange={(e) => setResetPwd(e.target.value)}
              placeholder="Nova senha (mín. 6 caracteres)"
              style={{ ...inputStyle, marginBottom: 16 }}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setResetTarget(null); setResetPwd(""); }} style={{
                padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff",
                color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>Cancelar</button>
              <button type="submit" disabled={resetBusy || resetPwd.length < 6} style={{
                padding: "7px 16px", borderRadius: 6, border: "none",
                background: "#f59e0b", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
                opacity: (resetBusy || resetPwd.length < 6) ? 0.6 : 1,
              }}>
                {resetBusy ? "Salvando..." : "Redefinir"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
